import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Clock, 
  MapPin, 
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp,
  Package,
  Calendar,
  User,
  Mail,
  Phone,
  Truck,
  CreditCard,
  ShoppingBag,
  Timer,
  AlertCircle
} from 'lucide-react';

const DeliveryHistory = () => {
  const [activeTab, setActiveTab] = useState('delivered');
  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [filteredDelivered, setFilteredDelivered] = useState([]);
  const [filteredPending, setFilteredPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  // Fetch all delivery history (without driver filtering)
  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    const fetchDeliveries = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          'https://afric-fresh-backend.onrender.com/admin/all-deliveries',
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: { 
              sortBy 
            }
          }
        );
        
        if (response.data) {
          setDeliveredOrders(response.data.deliveredOrders || []);
          setFilteredDelivered(response.data.deliveredOrders || []);
          setPendingOrders(response.data.pendingOrders || []);
          setFilteredPending(response.data.pendingOrders || []);
        } else {
          setDeliveredOrders([]);
          setFilteredDelivered([]);
          setPendingOrders([]);
          setFilteredPending([]);
        }
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch delivery history');
        setLoading(false);
      }
    };
  
    fetchDeliveries();
  }, [sortBy]);

  // Handle search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDelivered(deliveredOrders);
      setFilteredPending(pendingOrders);
      return;
    }
    
    const filteredDelivered = deliveredOrders.filter(
      delivery => 
        (delivery.orderDetails?.orderNumber && 
          delivery.orderDetails.orderNumber.toString().includes(searchQuery)) ||
        (delivery.customerName && delivery.customerName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (delivery.customerEmail && delivery.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    const filteredPending = pendingOrders.filter(
      delivery => 
        (delivery.orderDetails?.orderNumber && 
          delivery.orderDetails.orderNumber.toString().includes(searchQuery)) ||
        (delivery.driverName && delivery.driverName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (delivery.driverEmail && delivery.driverEmail.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    setFilteredDelivered(filteredDelivered);
    setFilteredPending(filteredPending);
  }, [searchQuery, deliveredOrders, pendingOrders]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm p-6 mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Admin Delivery Dashboard</h1>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-8">
            <button
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'delivered' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('delivered')}
            >
              <CheckCircle className="w-4 h-4" />
              Delivered Orders
              {deliveredOrders.length > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {deliveredOrders.length}
                </span>
              )}
            </button>
            <button
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'pending' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('pending')}
            >
              <Timer className="w-4 h-4" />
              Pending Deliveries
              {pendingOrders.length > 0 && (
                <span className="ml-2 bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {pendingOrders.length}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={`Search ${activeTab === 'delivered' ? 'delivered orders' : 'pending deliveries'}...`}
              className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="w-full md:w-48">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white transition"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {/* Error State */}
        {error && !loading && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-50 p-4 rounded-lg text-red-600 mb-4"
          >
            {error}
          </motion.div>
        )}
        
        {/* Active Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'delivered' ? (
            <motion.div
              key="delivered"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* No Delivered Results State */}
              {!loading && !error && filteredDelivered.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-gray-50 rounded-lg p-8 text-center"
                >
                  <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">No delivered orders found</h3>
                  <p className="text-gray-500">
                    {searchQuery 
                      ? `No results match "${searchQuery}". Try a different search term.` 
                      : "No deliveries have been completed yet."}
                  </p>
                </motion.div>
              )}
              
              {/* Delivered Cards */}
              {!loading && !error && filteredDelivered.length > 0 && (
                <motion.div
                  layout
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                >
                  <AnimatePresence>
                    {filteredDelivered.map((delivery) => {
                      const timeSinceDelivery = Math.floor(
                        (new Date() - new Date(delivery.deliveredAt)) / (1000 * 60 * 60 * 24)
                      );
                      
                      return (
                        <motion.div
                          key={delivery._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition"
                        >
                          <div className="flex items-start space-x-4">
                            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-lg font-semibold text-gray-800">
                                Order #{delivery.orderDetails?.orderNumber || "N/A"}
                              </p>
                              <div className="flex items-center space-x-2 mt-2">
                                <Truck className="w-4 h-4 text-gray-500" />
                                <p className="text-sm text-gray-600">
                                  Driver: {delivery.driverName || 'Unassigned'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 mt-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <p className="text-sm text-gray-600">
                                  {timeSinceDelivery === 0 
                                    ? 'Delivered today' 
                                    : `Delivered ${timeSinceDelivery} ${timeSinceDelivery === 1 ? 'day' : 'days'} ago`}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 mt-2">
                                <MapPin className="w-4 h-4 text-gray-500" />
                                <p className="text-sm text-gray-600">
                                  {delivery.orderDetails?.billingAddress?.city || 'N/A'}, {delivery.orderDetails?.billingAddress?.state || 'N/A'}
                                </p>
                              </div>
                              <p className="mt-2 text-sm font-semibold text-gray-700">
                                Total: ${delivery.orderDetails?.total || '0.00'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Display first item image */}
                          {delivery.orderDetails?.cartItems && delivery.orderDetails.cartItems.length > 0 && (
                            <div className="mt-4 flex items-center space-x-3">
                              {delivery.orderDetails.cartItems[0].img && (
                                <div className="w-14 h-14 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                                  <img 
                                    src={delivery.orderDetails.cartItems[0].img} 
                                    alt={delivery.orderDetails.cartItems[0].name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => e.target.src = 'https://placehold.co/100x100?text=No+Image'}
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {delivery.orderDetails.cartItems[0].name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {delivery.orderDetails.cartItems.length > 1 
                                    ? `+${delivery.orderDetails.cartItems.length - 1} more items` 
                                    : '1 item'}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <button
                            onClick={() => {
                              setSelectedDelivery({...delivery, type: 'delivered'});
                              setIsModalOpen(true);
                            }}
                            className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                          >
                            <span>View Details</span>
                          </button>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* No Pending Results State */}
              {!loading && !error && filteredPending.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-gray-50 rounded-lg p-8 text-center"
                >
                  <Timer className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">No pending deliveries found</h3>
                  <p className="text-gray-500">
                    {searchQuery 
                      ? `No results match "${searchQuery}". Try a different search term.` 
                      : "There are no pending deliveries at the moment."}
                  </p>
                </motion.div>
              )}
              
              {/* Pending Cards */}
              {!loading && !error && filteredPending.length > 0 && (
                <motion.div
                  layout
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                >
                  <AnimatePresence>
                    {filteredPending.map((delivery) => {
                      const timeElapsed = Math.floor(
                        (new Date() - new Date(delivery.acceptedAt)) / (1000 * 60 * 60)
                      );
                      
                      return (
                        <motion.div
                          key={delivery._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition"
                        >
                          <div className="flex items-start space-x-4">
                            <Timer className="w-6 h-6 text-amber-500 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-lg font-semibold text-gray-800">
                                  Order #{delivery.orderDetails?.orderNumber || "N/A"}
                                </p>
                                <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                                  Pending
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 mt-2">
                                <Truck className="w-4 h-4 text-gray-500" />
                                <p className="text-sm text-gray-600">
                                  Driver: {delivery.driverName || 'Unassigned'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 mt-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <p className="text-sm text-gray-600">
                                  {timeElapsed < 1 
                                    ? 'Accepted less than an hour ago' 
                                    : timeElapsed < 24
                                      ? `Accepted ${timeElapsed} ${timeElapsed === 1 ? 'hour' : 'hours'} ago`
                                      : `Accepted ${Math.floor(timeElapsed / 24)} ${Math.floor(timeElapsed / 24) === 1 ? 'day' : 'days'} ago`}
                                </p>
                              </div>
                              {delivery.orderDetails?.billingAddress && (
                                <div className="flex items-center space-x-2 mt-2">
                                  <MapPin className="w-4 h-4 text-gray-500" />
                                  <p className="text-sm text-gray-600">
                                    {delivery.orderDetails.billingAddress.city}, {delivery.orderDetails.billingAddress.state}
                                  </p>
                                </div>
                              )}
                              {delivery.orderDetails?.total && (
                                <p className="mt-2 text-sm font-semibold text-gray-700">
                                  Total: ${delivery.orderDetails.total}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Display first item image */}
                          {delivery.orderDetails?.cartItems && delivery.orderDetails.cartItems.length > 0 && (
                            <div className="mt-4 flex items-center space-x-3">
                              {delivery.orderDetails.cartItems[0].img && (
                                <div className="w-14 h-14 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                                  <img 
                                    src={delivery.orderDetails.cartItems[0].img} 
                                    alt={delivery.orderDetails.cartItems[0].name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => e.target.src = 'https://placehold.co/100x100?text=No+Image'}
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {delivery.orderDetails.cartItems[0].name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {delivery.orderDetails.cartItems.length > 1 
                                    ? `+${delivery.orderDetails.cartItems.length - 1} more items` 
                                    : '1 item'}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-4 flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedDelivery({...delivery, type: 'pending'});
                                setIsModalOpen(true);
                              }}
                              className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                            >
                              <span>View Details</span>
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      {/* Detail Modal */}
      <AnimatePresence>
        {isModalOpen && selectedDelivery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  {selectedDelivery.type === 'delivered' ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Order #{selectedDelivery.orderDetails?.orderNumber || "N/A"} Details
                    </>
                  ) : (
                    <>
                      <Timer className="w-5 h-5 text-amber-500" />
                      Pending Order #{selectedDelivery.orderDetails?.orderNumber || "N/A"} Details
                    </>
                  )}
                </h2>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6">
                {/* Delivery Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className={`${selectedDelivery.type === 'delivered' ? 'bg-blue-50' : 'bg-amber-50'} p-4 rounded-lg`}>
                    <h3 className={`font-medium ${selectedDelivery.type === 'delivered' ? 'text-blue-800' : 'text-amber-800'} mb-3 flex items-center gap-2`}>
                      <Calendar className="w-4 h-4" />
                      Delivery Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      {selectedDelivery.type === 'delivered' ? (
                        <>
                          <p><span className="text-gray-500">Delivery Date:</span> {formatDate(selectedDelivery.deliveredAt)}</p>
                          <p><span className="text-gray-500">Order Status:</span> <span className="text-green-600 font-medium">Delivered</span></p>
                          <div className="flex items-start gap-2 mt-2">
                            <div className="flex-shrink-0 mt-1">
                              <span className="text-gray-500">Driver:</span>
                            </div>
                            <div>
                              <p>{selectedDelivery.driverName || 'Unassigned'}</p>
                              <p className="text-sm text-gray-500">{selectedDelivery.driverEmail}</p>
                            </div>
                          </div>
                          {selectedDelivery.deliveryProof && (
                            <div className="flex items-start gap-2 mt-2">
                              <div className="flex-shrink-0 mt-1">
                                <span className="text-gray-500">Proof:</span>
                              </div>
                              <div>
                                <img
                                  src={selectedDelivery.deliveryProof}
                                  alt="Delivery Proof"
                                  className="w-24 h-24 object-cover rounded-md border border-gray-200 hover:opacity-90 cursor-pointer"
                                  onClick={() => window.open(selectedDelivery.deliveryProof, '_blank')}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <p><span className="text-gray-500">Accepted Date:</span> {formatDate(selectedDelivery.acceptedAt)}</p>
                          <p><span className="text-gray-500">Order Status:</span> <span className="text-amber-600 font-medium">Pending Delivery</span></p>
                          <div className="flex items-start gap-2 mt-2">
                            <div className="flex-shrink-0 mt-1">
                              <span className="text-gray-500">Driver:</span>
                            </div>
                            <div>
                              <p>{selectedDelivery.driverName || 'Unassigned'}</p>
                              <p className="text-sm text-gray-500">{selectedDelivery.driverEmail}</p>
                            </div>
                          </div>
                          <p className="text-gray-600 italic mt-1">
                            <AlertCircle className="w-4 h-4 inline mr-1" />
                            This order is awaiting delivery
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {selectedDelivery.type === 'delivered' ? 'Customer Information' : 'Order Information'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      {selectedDelivery.type === 'delivered' ? (
                        <>
                          <p><span className="text-gray-500">Name:</span> {selectedDelivery.customerName || 'N/A'}</p>
                          <p className="flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" />
                            <a href={`mailto:${selectedDelivery.customerEmail}`} className="text-blue-600 hover:underline">
                              {selectedDelivery.customerEmail || 'N/A'}
                            </a>
                          </p>
                          <p className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <a href={`tel:${selectedDelivery.customerPhone}`} className="text-blue-600 hover:underline">
                              {selectedDelivery.customerPhone || 'N/A'}
                            </a>
                          </p>
                        </>
                      ) : (
                        <>
                          <p><span className="text-gray-500">Customer Name:</span> {selectedDelivery.orderDetails?.billingAddress?.firstName || 'N/A'} {selectedDelivery.orderDetails?.billingAddress?.lastName || ''}</p>
                          <p className="flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" />
                            <span>{selectedDelivery.orderDetails?.email || 'N/A'}</span>
                          </p>
                          <p className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span>{selectedDelivery.orderDetails?.billingAddress?.phone || 'N/A'}</span>
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Only show these sections if we have order details */}
                {selectedDelivery.orderDetails && (
                  <>
                    {/* Shipping and Payment */}
                    {selectedDelivery.orderDetails.billingAddress && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                          <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Shipping Address
                          </h3>
                          <div className="bg-gray-50 p-4 rounded-lg text-sm">
                            <p className="font-medium">{selectedDelivery.orderDetails.billingAddress.firstName} {selectedDelivery.orderDetails.billingAddress.lastName}</p>
                            <p>{selectedDelivery.orderDetails.billingAddress.streetHouseApartment}</p>
                            {selectedDelivery.orderDetails.billingAddress.apartment && (
                              <p>Apt: {selectedDelivery.orderDetails.billingAddress.apartment}</p>
                            )}
                            <p>{selectedDelivery.orderDetails.billingAddress.city}, {selectedDelivery.orderDetails.billingAddress.state} {selectedDelivery.orderDetails.billingAddress.zipCode}</p>
                            <p>{selectedDelivery.orderDetails.billingAddress.country}</p>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Payment & Shipping Method
                          </h3>
                          <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
                            <p>
                              <span className="text-gray-500">Payment Method:</span> {selectedDelivery.orderDetails.paymentMethod || 'N/A'}
                            </p>
                            <p>
                              <span className="text-gray-500">Shipping Method:</span> {selectedDelivery.orderDetails.shippingMethod || 'N/A'}
                            </p>
                            {selectedDelivery.orderDetails.deliveryTime && (
                              <p>
                                <span className="text-gray-500">Delivery Time:</span> {selectedDelivery.orderDetails.deliveryTime}
                              </p>
                            )}
                            <p>
                              <span className="text-gray-500">Payment Status:</span> {selectedDelivery.orderDetails.paidsuccess === "true" ? "Paid" : "Pending"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Order Items */}
                    {selectedDelivery.orderDetails.cartItems && selectedDelivery.orderDetails.cartItems.length > 0 && (
                      <div className="mb-8">
                        <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4" />
                          Order Items
                        </h3>
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Item
                                </th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Price
                                </th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Quantity
                                </th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectedDelivery.orderDetails.cartItems.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      {item.img && (
                                        <div className="flex-shrink-0 h-10 w-10 mr-3">
                                          <img 
                                            className="h-10 w-10 rounded-md object-cover" 
                                            src={item.img} 
                                            alt={item.name} 
                                            onError={(e) => e.target.src = 'https://placehold.co/100x100?text=No+Image'}
                                          />
                                        </div>
                                      )}
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                        <div className="text-xs text-gray-500">{item.brand}</div>
                                        {item.orderType !== "General" && (
                                          <div className="text-xs text-blue-600">{item.orderType}</div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    ${item.price}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {item.quantity}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                    ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Order Summary */}
                    {selectedDelivery.orderDetails.total && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-medium text-gray-800 mb-3">Order Summary</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subtotal</span>
                            <span>${selectedDelivery.orderDetails.subtotal}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Shipping</span>
                            <span>${selectedDelivery.orderDetails.shippingCost}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-200 font-medium">
                            <span>Total</span>
                            <span>${selectedDelivery.orderDetails.total}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 font-medium transition"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeliveryHistory;